<template>
  <div class="autocomplete">
    <input
      ref="inputRef"
      v-model="inputText"
      type="text"
      class="form-control"
      @input="handleInput"
      @keydown.down="onArrowDown"
      @keydown.up="onArrowUp"
      @keydown.esc="onEsc"
      @keydown.enter="onEnter"
      @compositionstart="composing = true"
      @compositionend="composing = false"
      @focus="onFocus"
      @blur="onBlur"
    />
    <div v-if="shouldShowResults" class="results" :style="`width: ${inputWidth}px;`">
      <div
        v-for="(item, index) in filteredResults"
        :key="index"
        :ref="(el) => itemsRef[index] = el"
        :style="index === position ? 'background-color: #007BFF;' : ''"
        @mouseenter="onHover(index)"
        @click="onClick"
      >
        <template v-if="displayItemKey">
          <div class="row">
            <div class="result-item result-item-key col-2 text-secondary text-end">
              {{ displayItemKey(item) }}
            </div>
            <div class="result-item col-10 text-start">
              {{ displayItem(item) }}
            </div>
          </div>
        </template>
        <template v-else>
          <div class="result-item">
            {{ displayItem(item) }}
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import {onMounted, watch, ref, computed, onUnmounted} from 'vue';

const INITIAL_POSITION = -1;

/**
 * inputに関連する処理をまとめたcomposable
 */
function useInputRef() {
  // inputのDOM参照
  const inputRef = ref();

  const inputWidth = ref(0);
  /**
   * inputのサイズに合わせて選択肢表示エリア幅を調整する
   */
  const resizeInputWidth = () => {
    inputWidth.value = inputRef.value.offsetWidth;
  }
  onMounted(() => {
    // 選択肢表示エリアの初期widthを設定
    resizeInputWidth();

    // ブラウザのサイズ変更に合わせてinputのサイズも変わるので選択肢表示エリアを調整
    window.addEventListener('resize', resizeInputWidth);
  });
  onUnmounted(() => {
    window.removeEventListener('resize', resizeInputWidth);
  });

  return {
    inputRef,
    inputWidth,
  }
}

/**
 * 矢印キーに関連するイベントを制御するcomposable
 *
 * @param {Ref<UnwrapRef<number>>} position
 * @param {ComputedRef<number>} numResults
 */
function useHandleArrowKeyEvent(position, numResults) {
  // 補完候補へのDOM参照。矢印スクロール時に活用
  const itemsRef = ref([]);
  /**
   * 選択肢をスクロールさせる処理
   */
  const scrollItems = () => {
    if (0 <= position.value && position.value < numResults.value) {
      itemsRef.value[position.value].scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }

  /**
   * 矢印キーが押された時の処理
   */
  let arrowKeyTimeoutId;
  const onArrowKeyDown = (event, callback) => {
    event.stopPropagation();
    event.preventDefault();

    // マウスホバーと矢印による選択が競合するのでホバーを一時無効化
    document.body.style.pointerEvents = 'none';
    clearTimeout(arrowKeyTimeoutId);
    // 矢印入力の一定時間後にホバーを有効化
    arrowKeyTimeoutId = setTimeout(() => {
      document.body.style.pointerEvents = 'auto';
    }, 200)

    if (numResults.value <= 0) {
      return;
    }
    callback();
    scrollItems();

  }

  /**
   * 下矢印を押した時の処理
   * positionを更新する
   */
  const onArrowDown = (event) => {
    onArrowKeyDown(event, () => {
      position.value = (position.value + 1) % numResults.value;
    });
  };

  /**
   * 上矢印を押した時の処理
   * positionを更新する
   */
  const onArrowUp = (event) => {
    onArrowKeyDown(event, () => {
      position.value = (position.value - 1 + numResults.value) % numResults.value;
    });
  };

  return {
    itemsRef,
    onArrowDown,
    onArrowUp
  }
}

/**
 * 補完候補の表示を制御するcomposable
 *
 * @param {ComputedRef<number>} numResults
 */
function useResultsShown(numResults) {
  const isResultsShown = ref(false);
  const hideResults = () => {
    isResultsShown.value = false;
  };
  const showResults = () => {
    isResultsShown.value = true;
  };
  const shouldShowResults = computed(() => {
    return isResultsShown.value && numResults.value > 0;
  });

  return {
    hideResults,
    showResults,
    shouldShowResults,
  }
}

export default {
  name: 'Autocomplete',
  props: {
    // 初期に入力済みの文字列
    initialInputText: {
      type: String,
      default: () => '',
    },
    // 初期に選択済みのアイテム
    initialSelectedItem: {
      type: Object,
      default: () => null,
    },
    /**
     * リクエストの間隔を指定
     */
    debounce: {
      type: Number,
      default: 200,
    },
    /**
     * 補完選択肢のリスト
     */
    results: {
      type: Array,
      default: () => [],
    },
    /**
     * 補完の選択肢の表示方法を決める処理
     *
     * @param item
     * @returns {*}
     */
    displayItem: {
      type: Function,
      default: (item) => {
        return item.name;
      },
    },
    /**
     * 補完するテキストとは別にアイテムのidを表示したいときに使用
     * falseの場合はテキストのみ表示
     */
    displayItemKey: {
      type: Function,
      required: false,
    },
    /**
     * 補完選択肢の最大表示件数を指定する（未指定時は最大10件表示)
     */
    limit: {
      type: Number,
      default: 10,
    },
  },
  emits: ['select', 'update', 'blur'],
  setup(props, { emit }) {
    const { inputRef,inputWidth } = useInputRef();

    // 選択中の位置を保存する変数
    const position = ref(INITIAL_POSITION);

    // 選択肢の数
    const numResults = computed(() => props.results.length);

    // 入力中のテキストと選択されたアイテム
    const inputText = ref(props.initialInputText);
    const selectedItem = ref(props.initialSelectedItem);

    const { itemsRef, onArrowDown, onArrowUp } = useHandleArrowKeyEvent(position, numResults);

    /**
     * 選択中のアイテムを更新し、入力中テキストも同期させる
     */
    const updateSelectedItem = () => {
      if (0 <= position.value && position.value < numResults.value) {
        selectedItem.value = props.results[position.value];
        inputText.value = props.displayItem(selectedItem.value);
      } else {
        selectedItem.value = null;
      }
    };

    /**
     * 選択位置が変化したら選択中アイテムを更新する
     */
    watch(position, () => {
      updateSelectedItem();
    });

    /**
     * 選択決定時の処理（エンターやクリックなど）
     * selectイベントを発火する
     */
    const emitSelect = () => {
      // イベント発火前に選択中アイテムを最新にしておく
      updateSelectedItem();

      // 入力されたテキストと、選択されたアイテムを渡す
      emit('select', inputText.value, selectedItem.value);
    };

    // 日本語入力の変換確定前かどうか判定するための変数
    const composing = ref(false);
    /**
     * エンターを押した時の処理
     */
    const onEnter = () => {
      // 日本語変換時に選択肢決定処理が走らないようにする
      if (composing.value) {
        return;
      }
      inputRef.value.blur();
    };

    /**
     * 選択肢クリック時の処理
     */
    const onClick = () => {
      inputRef.value.blur();
    };

    /**
     * ESCキーを押した時の処理
     */
    const onEsc = () => {
      inputRef.value.blur();
    };

    const { showResults, hideResults, shouldShowResults } = useResultsShown(numResults);

    /**
     * フォーカスした時の処理
     */
    const onFocus = () => {
      showResults();
    };

    /**
     * フォーカスが外れた時の処理
     */
    const onBlur = () => {
      // フォーカスが外れた時点でinputに入っているテキストが選択されたとみなす。
      emitSelect();

      // 選択肢表示を隠す
      hideResults();

      emit('blur');
    };

    /**
     * 入力変更時の処理
     *
     * debounceによって一定時間経つまでリクエストを遅らせ、大量のリクエストを送るのを防ぐ
     * 入力が変わるたびにpositionをリセットする
     */
    let timeout; // timeoutリセット用にsetTimeoutの返り値を保持
    let isHoverEnabled = true; // ホバー処理の有効・無効を管理する変数
    const handleInput = (event) => {
      // ホバーが有効になっていると選択肢の数が変わった際に
      // 意図しないアイテムにマウスが乗って選択してしまうことがあるので無効化する
      isHoverEnabled = false;

      // 直前のsetTimeoutがまだ残っていればリセット
      clearTimeout(timeout);

      timeout = setTimeout(() => {
        // ポジションをリセットし、未選択状態にする
        position.value = INITIAL_POSITION;

        emit('update', event.target.value);

        // ホバーを再び有効化する
        isHoverEnabled = true;
      }, props.debounce);
    };

    /**
     * ホバー時の処理
     *
     * @param index ホバーした要素
     */
    const onHover = (index) => {
      // ホバーが有効なときのみpositionを更新する
      if (isHoverEnabled) {
        position.value = index;
      }
    }

    /**
     * limitで指定した数まで選択肢を表示する
     */
    const filteredResults = computed(() => {
      if (numResults.value > props.limit) {
        return props.results.slice(0, props.limit);
      }
      return props.results;
    });

    return {
      inputRef,
      itemsRef,
      inputWidth,
      inputText,
      handleInput,
      position,
      onArrowDown,
      onArrowUp,
      onEnter,
      onClick,
      onFocus,
      onBlur,
      onEsc,
      onHover,
      shouldShowResults,
      composing,
      filteredResults,
    };
  },
};
</script>

<style lang="scss" scoped>
.autocomplete {
  position: relative;
}

.results {
  position: absolute;
  top: 27px;
  border: 1px solid #ddd;
  z-index: 1000;
  background: white;
  max-height: 200px;
  overflow-y: auto;
}

.result-item {
  padding: 5px;
  cursor: pointer;
}

.result-item-key {
  border-right: 1px solid #ddd;
}
</style>
